import "./DateTimeModal.scss"

import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import Calendar, { CalendarProps } from "react-calendar"
import { range } from "lodash"
import { t } from "i18next"
import dayjs, { Dayjs } from "utils/dayjs"
import cx from "classnames"

import i18n from "intl/i18n"
import { toDateTimeWithoutTimezone } from "utils/utils"
import { Flex, FlexItem } from "components/Flex"
import Button from "components/Button/Button"
import Modal, { IModalProps } from "components/Modal/Modal"
import Spinner from "components/Spinner/Spinner"
import useClickOutside from "hooks/useClickOutside"

export type DateTimeType = "date" | "datetime"

export interface IDateTimeModalProps {
    type?: DateTimeType
    date?: Dayjs | null
    minDate?: Dayjs | null
    maxDate?: Dayjs | null
    minDetail?: CalendarProps["minDetail"]
    maxDetail?: CalendarProps["maxDetail"]
    defaultActiveStartDate?: CalendarProps["defaultActiveStartDate"]
    defaultHours?: number
    defaultMinutes?: number
    minutesStep?: number
    noSaveButton?: boolean
    saveFullWidth?: boolean
    utc?: boolean
    testIdPrefix?: string
    onChange: (date: Dayjs | null) => Promise<void> | void
    onClose: IModalProps["onClose"]
}

const DateTimeModal: FC<IDateTimeModalProps> = ({
    type = "date",
    date,
    minDate,
    maxDate,
    minDetail = "decade",
    maxDetail,
    defaultActiveStartDate,
    minutesStep = 1,
    defaultHours = 0,
    defaultMinutes = 0,
    utc,
    noSaveButton,
    saveFullWidth,
    testIdPrefix = "date-time-modal",
    onChange,
    onClose,
}) => {
    const getDate = useCallback((dt?: Dayjs | null) => {
        if (!dt || !dt.isValid()) return null
        return utc ? dayjs(toDateTimeWithoutTimezone(dt)).utc() : dt?.clone().local()
    }, [utc])

    const toCalendarDate = useCallback((dt?: Dayjs | null) => {
        const value = getDate(dt)
        if (!value) return undefined
        return new Date(value.year(), value.month(), value.date(), 12, 0)
    }, [getDate])

    const [changedDate, setChangedDate] = useState<Dayjs | null>(getDate(date))
    const [isTimeChanged, setIsTimeChanged] = useState(false)
    const [isApplyingDate, setIsApplyingDate] = useState(false)
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false)
    const [timePickerPosition, setTimePickerPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 })
    const timePickerRef = useRef<HTMLDivElement>(null)
    const [timePickerPopupEl, setTimePickerPopupEl] = useState<HTMLDivElement | null>(null)
    const hoursColumnRef = useRef<HTMLUListElement>(null)
    const minutesColumnRef = useRef<HTMLUListElement>(null)
    const isTimePickerDisabled = isApplyingDate
    const isSaveButtonDisabled = !changedDate || isApplyingDate
    const isSaveButtonLoading = isApplyingDate

    useEffect(() => {
        setChangedDate(getDate(date))
    }, [getDate, date, type])


    async function handleClose() {
        if (noSaveButton) {
            setIsApplyingDate(true)
            await onChange(getDate(changedDate))
            setIsApplyingDate(false)
        }
        onClose?.()
    }

    async function handleSave(dateToSave: Dayjs | null) {
        setIsApplyingDate(true)
        await onChange(getDate(dateToSave))
        setIsApplyingDate(false)
        onClose?.()
    }


    function handleDateChange(dt: Dayjs | null) {
        let newDate: Dayjs | null = getDate(dt)
        if (dt) {
            if (isTimeChanged) {
                if (changedDate) {
                    newDate = dayjs(getDate(dt)).hour(getDate(changedDate)!.hour()).minute(getDate(changedDate)!.minute())
                }
            } else {
                newDate = dayjs(getDate(dt)).hour(defaultHours).minute(defaultMinutes)
            }
        }

        let newChangedDate = newDate
        if (minDate && newDate?.isSameOrBefore(getDate(minDate))) {
            newChangedDate = dayjs(getDate(minDate))
        } else if (maxDate && newDate?.isSameOrAfter(getDate(maxDate))) {
            newChangedDate = dayjs(getDate(maxDate))
        }
        setChangedDate(newChangedDate)

        if (type === "date" && noSaveButton) {
            handleSave(newChangedDate)
        }
    }



    function handleTimeChange(newDate: Dayjs | null) {
        if (minDate && getDate(newDate)?.isSameOrBefore(getDate(minDate))) {
            setChangedDate(dayjs(getDate(minDate)))
        } else if (maxDate && getDate(newDate)?.isSameOrAfter(getDate(maxDate))) {
            setChangedDate(dayjs(getDate(maxDate)))
        } else {
            setChangedDate(getDate(newDate))
        }
        setIsTimeChanged(true)
    }

    function selectHour(hour: number) {
        handleTimeChange(dayjs(getDate(changedDate)).hour(hour))
    }

    function selectMinute(minute: number) {
        handleTimeChange(dayjs(getDate(changedDate)).minute(minute))
    }

    function toggleTimePicker() {
        if (isTimePickerDisabled) return
        if (!isTimePickerOpen) {
            const rect = timePickerRef.current?.getBoundingClientRect()
            if (rect) setTimePickerPosition({ top: rect.bottom + 4, left: rect.left })
        }
        setIsTimePickerOpen(open => !open)
    }

    const currentHour = getDate(changedDate)?.hour() ?? 0
    const currentMinute = getDate(changedDate)?.minute() ?? 0
    const hourOptions = useMemo(() => range(0, 24), [])
    const minuteOptions = useMemo(() => range(0, 60, minutesStep), [minutesStep])

    const disabledHours = useMemo(() => {
        const current = getDate(changedDate)
        const start = minDate && current?.isSame(getDate(minDate), "date") ? range(0, getDate(minDate)!.hour()) : []
        const end = maxDate && current?.isSame(getDate(maxDate), "date") ? range(getDate(maxDate)!.hour() + 1, 24) : []
        return [...start, ...end]
    }, [changedDate, minDate, maxDate, getDate])

    const disabledMinutes = useMemo(() => {
        const current = getDate(changedDate)
        const start = minDate && current?.isSame(getDate(minDate), "hour") ? range(0, getDate(minDate)!.minute()) : []
        const end = maxDate && current?.isSame(getDate(maxDate), "hour") ? range(getDate(maxDate)!.minute() + 1, 60) : []
        return [...start, ...end]
    }, [changedDate, minDate, maxDate, getDate])

    useClickOutside(() => setIsTimePickerOpen(false), timePickerRef.current, [timePickerPopupEl])

    useEffect(() => {
        if (!isTimePickerOpen) return
        for (const column of [hoursColumnRef.current, minutesColumnRef.current]) {
            const selected = column?.querySelector<HTMLElement>("[data-selected='true']")
            if (column && selected) column.scrollTop = selected.offsetTop
        }
    }, [isTimePickerOpen])

    const saveButton = (
        <Button
            label={t('common.save')}
            disabled={isSaveButtonDisabled}
            loading={isSaveButtonLoading}
            onClick={() => handleSave(changedDate)}
            className={saveFullWidth ? "w-100" : undefined}
            data-testid={`${testIdPrefix}-save`}
        />
    )

    return (
        <Modal
            onClose={isApplyingDate ? undefined : handleClose}
            closeButton={false}
            sizeToContent>
            <div className="date-time-modal__calendar"
                data-testid={`${testIdPrefix}-calendar`}>
                <Calendar
                    defaultValue={toCalendarDate(changedDate)}
                    onChange={(newDate) => handleDateChange(newDate && !(newDate instanceof Array) ? dayjs(newDate) : null)}
                    formatShortWeekday={(locale, dt) => dayjs(dt).format("dd")}
                    minDetail={minDetail}
                    maxDetail={maxDetail}
                    defaultActiveStartDate={defaultActiveStartDate && !isNaN(defaultActiveStartDate.getTime()) ? defaultActiveStartDate : undefined}
                    minDate={toCalendarDate(minDate)}
                    maxDate={toCalendarDate(maxDate)}
                    locale={i18n.language}
                    prevLabel=""
                    nextLabel=""
                    prev2Label={null}
                    next2Label={null}
                    showWeekNumbers
                    tileDisabled={isApplyingDate ? () => true : undefined}
                />
                {isApplyingDate &&
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        className="date-time-modal__calendar-spinner">
                        <Spinner />
                    </Flex>
                }
            </div>
            {type === "date"  && !noSaveButton &&
                <Flex
                    className="w-100 mt-3"
                    justifyContent="flex-end">
                    {saveButton}
                </Flex>
            }
            {type === "datetime" &&
                <Flex
                    className="w-100 mt-3"
                    justifyContent="space-between"
                    alignItems="center"
                    gap="0.8rem">
                    <FlexItem className="w-100">
                        {changedDate &&
                            <div className="date-time-modal__time-control"
                                ref={timePickerRef}
                                data-testid={`${testIdPrefix}-time`}>
                                <div
                                    className={cx([
                                        "date-time-modal__time",
                                        {
                                            "date-time-modal__time--fullwidth": noSaveButton,
                                            "date-time-modal__time--open": isTimePickerOpen,
                                        },
                                    ])}
                                    onClick={toggleTimePicker}>
                                    {getDate(changedDate)!.format("HH:mm")}
                                </div>
                                {isTimePickerOpen && !isTimePickerDisabled && createPortal(
                                    <div
                                        ref={setTimePickerPopupEl}
                                        style={{ top: timePickerPosition.top, left: timePickerPosition.left }}
                                        className={cx([
                                            "date-time-modal__time-popup",
                                            { "date-time-modal__time-popup--fullwidth": noSaveButton },
                                        ])}>
                                        <ul className="date-time-modal__time-column" ref={hoursColumnRef} data-testid={`${testIdPrefix}-hours`}>
                                            {hourOptions.map(hour => (
                                                <li key={hour}>
                                                    <button
                                                        type="button"
                                                        data-selected={hour === currentHour}
                                                        disabled={disabledHours.includes(hour)}
                                                        className={cx([
                                                            "date-time-modal__time-option",
                                                            { "date-time-modal__time-option--selected": hour === currentHour },
                                                        ])}
                                                        onClick={() => selectHour(hour)}>
                                                        {String(hour).padStart(2, "0")}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                        <ul className="date-time-modal__time-column" ref={minutesColumnRef} data-testid={`${testIdPrefix}-minutes`}>
                                            {minuteOptions.map(minute => (
                                                <li key={minute}>
                                                    <button
                                                        type="button"
                                                        data-selected={minute === currentMinute}
                                                        disabled={disabledMinutes.includes(minute)}
                                                        className={cx([
                                                            "date-time-modal__time-option",
                                                            { "date-time-modal__time-option--selected": minute === currentMinute },
                                                        ])}
                                                        onClick={() => selectMinute(minute)}>
                                                        {String(minute).padStart(2, "0")}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>,
                                    document.body
                                )}
                            </div>
                        }
                    </FlexItem>
                    {!noSaveButton &&
                        <FlexItem shrink={0}>
                            {saveButton}
                        </FlexItem>
                    }
                </Flex>
            }
        </Modal>
    )
}

export default DateTimeModal
